import { useState } from 'react';
import {
  getImageScannerDownloadUrl,
  getImageScannerAttempts,
  getImageScannerMessage,
  getPassageScannerMessage,
  getPassageScannerRawXml,
  getResultatScannerMessage,
  getResultatScannerRawXml,
  listImageScannerMessages,
  listPassageScannerMessages,
  listResultatScannerMessages,
  reprocessPassageScannerMessage,
  resendImageScannerMessage,
  resendResultatScannerMessage,
  type ImageScannerListQuery,
  type PassageScannerListQuery,
  type ResultatScannerListQuery,
} from '../api/scanner';
import { useAsyncData } from '../hooks/useAsyncData';
import { ErrorState, LoadingState } from '../components/RequestState';
import { JsonBlock } from '../components/JsonBlock';
import { StatusBadge } from '../components/StatusBadge';
import { ConfirmDialog, type ConfirmDialogState } from '../components/ConfirmDialog';
import { PageHeader } from '../components/FeatureStatus';
import { CopyButton } from '../components/CopyButton';
import { usePermission } from '../hooks/usePermission';

type ScannerFamily = 'PASSAGE_SCANNER' | 'RESULTAT_SCANNER' | 'IMAGE_SCANNER';

const SCANNER_PAGE_SIZE = 20;

const FAMILY_TABS: Array<{ key: ScannerFamily; label: string }> = [
  { key: 'PASSAGE_SCANNER', label: 'Passage Scanner (ADII → NWM)' },
  { key: 'RESULTAT_SCANNER', label: 'Résultat Scanner (NWM → ADII)' },
  { key: 'IMAGE_SCANNER', label: 'Image Scanner (NWM → ADII)' },
];

export function ScannerPage() {
  const [family, setFamily] = useState<ScannerFamily>('PASSAGE_SCANNER');

  return (
    <div className="page-grid">
      <PageHeader
        eyebrow="Maritime"
        title="Scanner — Image / Passage / Résultat"
        purpose="Inspect the three ADII Scanner message families (IMAGE_SCANNER, PASSAGE_SCANNER, RESULTAT_SCANNER) and their functional acknowledgements. Business-user counters and image previews live in the dedicated Scanner Monitor console; this page is the technical/operational view (raw XML, reprocess, ACK status)."
        status="PRODUCTION_READY"
        connected
        next="Use reprocess only after confirming the underlying issue has been resolved."
      />
      <div className="filter-bar">
        {FAMILY_TABS.map((tab) => (
          <button
            key={tab.key}
            type="button"
            className={family === tab.key ? 'link-button active' : 'link-button'}
            onClick={() => setFamily(tab.key)}
          >
            {tab.label}
          </button>
        ))}
      </div>
      {family === 'PASSAGE_SCANNER' ? <PassageScannerSection /> : null}
      {family === 'RESULTAT_SCANNER' ? <ResultatScannerSection /> : null}
      {family === 'IMAGE_SCANNER' ? <ImageScannerSection /> : null}
    </div>
  );
}

// ---------------------------------------------------------------------------
// PASSAGE_SCANNER
// ---------------------------------------------------------------------------

function PassageScannerSection() {
  const [filters, setFilters] = useState<PassageScannerListQuery>({ page: 1, pageSize: SCANNER_PAGE_SIZE });
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const { data, isLoading, error, refresh } = useAsyncData(() => listPassageScannerMessages(filters), [JSON.stringify(filters)]);
  const total = data?.total ?? 0;
  const page = filters.page ?? 1;
  const totalPages = Math.max(1, Math.ceil(total / SCANNER_PAGE_SIZE));

  return (
    <section className="panel">
      <div className="panel-header">
        <h2>Passage Scanner messages</h2>
        <div className="panel-actions">
          <button type="button" onClick={() => refresh()}>Refresh</button>
        </div>
      </div>
      <div className="filter-bar">
        <label className="filter-field">
          <span>Reference declaration</span>
          <input
            value={filters.referenceDeclaration ?? ''}
            onChange={(event) => setFilters((current) => ({ ...current, page: 1, referenceDeclaration: event.target.value }))}
          />
        </label>
        <label className="filter-field">
          <span>ACK status</span>
          <select
            value={filters.ackStatus ?? ''}
            onChange={(event) => setFilters((current) => ({ ...current, page: 1, ackStatus: event.target.value }))}
          >
            <option value="">All</option>
            <option value="PENDING">Pending</option>
            <option value="ACK_OK">ACK OK</option>
            <option value="ACK_NOK">ACK NOK</option>
            <option value="ACK_TIMEOUT">ACK Timeout</option>
            <option value="NOT_REQUIRED">Not required</option>
          </select>
        </label>
      </div>
      {isLoading ? <LoadingState /> : null}
      {error ? <ErrorState message={error} onRetry={refresh} /> : null}
      {!isLoading && !error ? (
        <table className="data-table">
          <thead>
            <tr><th>Reference declaration</th><th>Registration</th><th>Conformité</th><th>Processing</th><th>ACK</th><th>Received</th></tr>
          </thead>
          <tbody>
            {(data?.data ?? []).map((message) => (
              <tr key={message.id}>
                <td><button type="button" className="link-button" onClick={() => setSelectedId(message.id)}><span className="mono">{message.referenceDeclaration ?? message.id}</span></button></td>
                <td className="mono">{message.numeroImmatriculation ?? 'n/a'}</td>
                <td>{message.resultatConformite ?? 'n/a'}</td>
                <td><StatusBadge value={message.processingStatus} /></td>
                <td><StatusBadge value={message.ackStatus} /></td>
                <td>{new Date(message.createdAt).toLocaleString('en-GB')}</td>
              </tr>
            ))}
            {(data?.data ?? []).length === 0 ? <tr><td colSpan={6}>No PASSAGE_SCANNER message matches the current filters.</td></tr> : null}
          </tbody>
        </table>
      ) : null}
      {total > SCANNER_PAGE_SIZE ? (
        <div className="pagination-bar">
          <button type="button" className="ghost-button" disabled={page <= 1 || isLoading} onClick={() => setFilters((current) => ({ ...current, page: page - 1 }))}>Previous</button>
          <span>Page {page} of {totalPages} &mdash; {total} total</span>
          <button type="button" className="ghost-button" disabled={page >= totalPages || isLoading} onClick={() => setFilters((current) => ({ ...current, page: page + 1 }))}>Next</button>
        </div>
      ) : null}
      {selectedId ? <PassageScannerDetailPanel key={selectedId} id={selectedId} onClose={() => setSelectedId(null)} onChanged={() => refresh()} /> : null}
    </section>
  );
}

function PassageScannerDetailPanel({ id, onClose, onChanged }: { id: string; onClose: () => void; onChanged: () => void }) {
  const canOperate = usePermission('flow.operations.execute');
  const [confirm, setConfirm] = useState<ConfirmDialogState | null>(null);
  const [rawXml, setRawXml] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const { data, isLoading, error, refresh } = useAsyncData(() => getPassageScannerMessage(id), [id]);

  async function handleReprocess() {
    await reprocessPassageScannerMessage(id);
    setRawXml(null);
    await refresh();
    onChanged();
  }

  async function handleToggleRawXml() {
    if (rawXml !== null) {
      setRawXml(null);
      return;
    }
    try {
      setActionError(null);
      setRawXml(await getPassageScannerRawXml(id));
    } catch (caught) {
      setActionError(caught instanceof Error ? caught.message : 'Unable to load raw XML.');
    }
  }

  if (isLoading) return <LoadingState />;
  if (error) return <ErrorState message={error} onRetry={refresh} />;
  if (!data) return null;

  return (
    <section className="panel">
      <div className="panel-header">
        <div><h2>Passage Scanner detail</h2><p className="mono">{data.id}</p></div>
        <div className="panel-actions">
          {canOperate ? <button type="button" onClick={() => setConfirm({
            title: 'Reprocess PASSAGE_SCANNER message',
            message: 'This re-runs validation from the stored raw XML and resubmits ACK_PS to ADII.',
            confirmLabel: 'Reprocess',
            risk: 'medium',
            onConfirm: () => handleReprocess(),
          })}>Reprocess</button> : null}
          <button type="button" onClick={() => void handleToggleRawXml()}>
            {rawXml === null ? 'Show raw XML' : 'Hide raw XML'}
          </button>
          <button type="button" onClick={onClose}>Close</button>
        </div>
      </div>
      <div className="summary-grid">
        <div><strong>Reference declaration</strong><span className="mono">{data.referenceDeclaration ?? 'n/a'}</span></div>
        <div><strong>Identifiant passage</strong><span className="mono">{data.identifiantPassageScanner ?? 'n/a'}</span></div>
        <div><strong>Registration</strong><span className="mono">{data.numeroImmatriculation ?? 'n/a'}</span></div>
        <div><strong>Conformité</strong><span>{data.resultatConformite ?? 'n/a'}</span></div>
        <div><strong>Validation</strong><span><StatusBadge value={data.validationStatus} /></span></div>
        <div><strong>Processing</strong><span><StatusBadge value={data.processingStatus} /></span></div>
        <div><strong>ACK status</strong><span><StatusBadge value={data.ackStatus} /></span></div>
        <div><strong>Correlation ID</strong><span className="inline-copy"><span className="mono">{data.correlationId}</span><CopyButton value={data.correlationId} label="Copy correlation ID" /></span></div>
      </div>
      {data.ackFailureReason ? <div className="form-error">{data.ackFailureReason}</div> : null}
      {actionError ? <div className="form-error">{actionError}</div> : null}
      {rawXml !== null ? (<><h3>Raw XML</h3><pre className="code-block content-viewer raw-payload-viewer">{rawXml}</pre></>) : null}
      {data.normalizedJson ? (<><h3>Normalized JSON</h3><JsonBlock value={data.normalizedJson} /></>) : null}
      <ConfirmDialog state={confirm} onClose={() => setConfirm(null)} />
    </section>
  );
}

// ---------------------------------------------------------------------------
// RESULTAT_SCANNER
// ---------------------------------------------------------------------------

function ResultatScannerSection() {
  const [filters, setFilters] = useState<ResultatScannerListQuery>({ page: 1, pageSize: SCANNER_PAGE_SIZE });
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const { data, isLoading, error, refresh } = useAsyncData(() => listResultatScannerMessages(filters), [JSON.stringify(filters)]);
  const total = data?.total ?? 0;
  const page = filters.page ?? 1;
  const totalPages = Math.max(1, Math.ceil(total / SCANNER_PAGE_SIZE));

  return (
    <section className="panel">
      <div className="panel-header">
        <h2>Résultat Scanner messages</h2>
        <div className="panel-actions"><button type="button" onClick={() => refresh()}>Refresh</button></div>
      </div>
      <div className="filter-bar">
        <label className="filter-field">
          <span>Reference declaration</span>
          <input value={filters.referenceDeclaration ?? ''} onChange={(event) => setFilters((current) => ({ ...current, page: 1, referenceDeclaration: event.target.value }))} />
        </label>
        <label className="filter-field">
          <span>Scan identifier</span>
          <input value={filters.identifiantScannage ?? ''} onChange={(event) => setFilters((current) => ({ ...current, page: 1, identifiantScannage: event.target.value }))} />
        </label>
        <label className="filter-field">
          <span>ACK status</span>
          <select
            value={filters.status ?? ''}
            onChange={(event) => setFilters((current) => ({ ...current, page: 1, status: event.target.value }))}
          >
            <option value="">All</option>
            <option value="PENDING">Pending</option>
            <option value="ACK_OK">ACK OK</option>
            <option value="ACK_NOK">ACK NOK</option>
            <option value="ACK_TIMEOUT">ACK Timeout</option>
            <option value="ACK_ORPHAN">ACK Orphan</option>
            <option value="ACK_CONFLICT">ACK Conflict</option>
          </select>
        </label>
      </div>
      {isLoading ? <LoadingState /> : null}
      {error ? <ErrorState message={error} onRetry={refresh} /> : null}
      {!isLoading && !error ? (
        <table className="data-table">
          <thead><tr><th>Reference declaration</th><th>Scan id</th><th>Type</th><th>Résultat</th><th>Status</th><th>Submitted</th></tr></thead>
          <tbody>
            {(data?.data ?? []).map((message) => (
              <tr key={message.id}>
                <td><button type="button" className="link-button" onClick={() => setSelectedId(message.id)}><span className="mono">{message.referenceDeclaration}</span></button></td>
                <td className="mono">{message.identifiantScannage}</td>
                <td>{message.typeDeclaration}</td>
                <td><StatusBadge value={message.resultat === 'S' ? 'SUSPECT' : 'NON_SUSPECT'} /></td>
                <td><StatusBadge value={message.status} /></td>
                <td>{new Date(message.createdAt).toLocaleString('en-GB')}</td>
              </tr>
            ))}
            {(data?.data ?? []).length === 0 ? <tr><td colSpan={6}>No RESULTAT_SCANNER message matches the current filters.</td></tr> : null}
          </tbody>
        </table>
      ) : null}
      {total > SCANNER_PAGE_SIZE ? (
        <div className="pagination-bar">
          <button type="button" className="ghost-button" disabled={page <= 1 || isLoading} onClick={() => setFilters((current) => ({ ...current, page: page - 1 }))}>Previous</button>
          <span>Page {page} of {totalPages} &mdash; {total} total</span>
          <button type="button" className="ghost-button" disabled={page >= totalPages || isLoading} onClick={() => setFilters((current) => ({ ...current, page: page + 1 }))}>Next</button>
        </div>
      ) : null}
      {selectedId ? <ResultatScannerDetailPanel key={selectedId} id={selectedId} onClose={() => setSelectedId(null)} /> : null}
    </section>
  );
}

function ResultatScannerDetailPanel({ id, onClose }: { id: string; onClose: () => void }) {
  const canOperate = usePermission('flow.operations.execute');
  const [rawXml, setRawXml] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const { data, isLoading, error, refresh } = useAsyncData(() => getResultatScannerMessage(id), [id]);

  async function handleResend() {
    try {
      setActionError(null);
      await resendResultatScannerMessage(id);
      await refresh();
    } catch (caught) {
      setActionError(caught instanceof Error ? caught.message : 'Unable to resend message.');
    }
  }

  async function handleToggleRawXml() {
    if (rawXml !== null) {
      setRawXml(null);
      return;
    }
    try {
      setActionError(null);
      setRawXml(await getResultatScannerRawXml(id));
    } catch (caught) {
      setActionError(caught instanceof Error ? caught.message : 'Unable to load raw XML.');
    }
  }

  if (isLoading) return <LoadingState />;
  if (error) return <ErrorState message={error} onRetry={refresh} />;
  if (!data) return null;

  return (
    <section className="panel">
      <div className="panel-header">
        <div><h2>Résultat Scanner detail</h2><p className="mono">{data.id}</p></div>
        <div className="panel-actions">
          {canOperate && ['DEAD_LETTERED', 'DELIVERY_FAILED'].includes(data.status) ? <button type="button" onClick={() => void handleResend()}>Resend</button> : null}
          <button type="button" onClick={() => void handleToggleRawXml()}>
            {rawXml === null ? 'Show raw XML' : 'Hide raw XML'}
          </button>
          <button type="button" onClick={onClose}>Close</button>
        </div>
      </div>
      <div className="summary-grid">
        <div><strong>Scan identifier</strong><span className="mono">{data.identifiantScannage}</span></div>
        <div><strong>AMP</strong><span className="mono">{data.numeroAMP}</span></div>
        <div><strong>Declaration type</strong><span>{data.typeDeclaration}</span></div>
        <div><strong>Reference declaration</strong><span className="mono">{data.referenceDeclaration}</span></div>
        <div><strong>Résultat</strong><span>{data.resultat}</span></div>
        <div><strong>Status</strong><span><StatusBadge value={data.status} /></span></div>
        <div><strong>Correlation ID</strong><span className="inline-copy"><span className="mono">{data.correlationId ?? 'n/a'}</span><CopyButton value={data.correlationId} label="Copy correlation ID" /></span></div>
      </div>
      {data.failureReason ? <div className="form-error">{data.failureReason}</div> : null}
      {actionError ? <div className="form-error">{actionError}</div> : null}
      {rawXml !== null ? (<><h3>Raw XML</h3><pre className="code-block content-viewer raw-payload-viewer">{rawXml}</pre></>) : null}
      {data.ackJson ? (<><h3>ACK_SCANNER</h3><JsonBlock value={data.ackJson} /></>) : null}
    </section>
  );
}

// ---------------------------------------------------------------------------
// IMAGE_SCANNER
// ---------------------------------------------------------------------------

function ImageScannerSection() {
  const [filters, setFilters] = useState<ImageScannerListQuery>({ page: 1, pageSize: SCANNER_PAGE_SIZE });
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const { data, isLoading, error, refresh } = useAsyncData(() => listImageScannerMessages(filters), [JSON.stringify(filters)]);
  const total = data?.total ?? 0;
  const page = filters.page ?? 1;
  const totalPages = Math.max(1, Math.ceil(total / SCANNER_PAGE_SIZE));

  return (
    <section className="panel">
      <div className="panel-header">
        <h2>Image Scanner messages</h2>
        <div className="panel-actions"><button type="button" onClick={() => refresh()}>Refresh</button></div>
      </div>
      <div className="filter-bar">
        <label className="filter-field">
          <span>AMP</span>
          <input value={filters.numeroAMP ?? ''} onChange={(event) => setFilters((current) => ({ ...current, page: 1, numeroAMP: event.target.value }))} />
        </label>
        <label className="filter-field">
          <span>Identifiant</span>
          <input value={filters.identifiant ?? ''} onChange={(event) => setFilters((current) => ({ ...current, page: 1, identifiant: event.target.value }))} />
        </label>
      </div>
      {isLoading ? <LoadingState /> : null}
      {error ? <ErrorState message={error} onRetry={refresh} /> : null}
      {!isLoading && !error ? (
        <table className="data-table">
          <thead><tr><th>AMP</th><th>Identifiant</th><th>Images</th><th>Status</th><th>Submitted</th></tr></thead>
          <tbody>
            {(data?.data ?? []).map((message) => (
              <tr key={message.id}>
                <td><button type="button" className="link-button" onClick={() => setSelectedId(message.id)}><span className="mono">{message.numeroAMP}</span></button></td>
                <td className="mono">{message.identifiant}</td>
                <td>{message.imageCount}</td>
                <td><StatusBadge value={message.status} /></td>
                <td>{new Date(message.createdAt).toLocaleString('en-GB')}</td>
              </tr>
            ))}
            {(data?.data ?? []).length === 0 ? <tr><td colSpan={5}>No IMAGE_SCANNER message matches the current filters.</td></tr> : null}
          </tbody>
        </table>
      ) : null}
      {total > SCANNER_PAGE_SIZE ? (
        <div className="pagination-bar">
          <button type="button" className="ghost-button" disabled={page <= 1 || isLoading} onClick={() => setFilters((current) => ({ ...current, page: page - 1 }))}>Previous</button>
          <span>Page {page} of {totalPages} &mdash; {total} total</span>
          <button type="button" className="ghost-button" disabled={page >= totalPages || isLoading} onClick={() => setFilters((current) => ({ ...current, page: page + 1 }))}>Next</button>
        </div>
      ) : null}
      {selectedId ? <ImageScannerDetailPanel key={selectedId} id={selectedId} onClose={() => setSelectedId(null)} /> : null}
    </section>
  );
}

function ImageScannerDetailPanel({ id, onClose }: { id: string; onClose: () => void }) {
  const canOperate = usePermission('flow.operations.execute');
  const { data, isLoading, error, refresh } = useAsyncData(() => getImageScannerMessage(id), [id]);
  const { data: attempts } = useAsyncData(() => getImageScannerAttempts(id), [id]);
  const [imageUrls, setImageUrls] = useState<Record<string, string>>({});
  const [actionError, setActionError] = useState<string | null>(null);

  async function handleDownload(imageId: string) {
    try {
      setActionError(null);
      const url = await getImageScannerDownloadUrl(id, imageId);
      setImageUrls((current) => ({ ...current, [imageId]: url }));
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch (caught) {
      setActionError(caught instanceof Error ? caught.message : 'Unable to download image.');
    }
  }

  async function handleResend() {
    try {
      setActionError(null);
      await resendImageScannerMessage(id);
      await refresh();
    } catch (caught) {
      setActionError(caught instanceof Error ? caught.message : 'Unable to resend message.');
    }
  }

  if (isLoading) return <LoadingState />;
  if (error) return <ErrorState message={error} onRetry={refresh} />;
  if (!data) return null;

  return (
    <section className="panel">
      <div className="panel-header">
        <div><h2>Image Scanner detail</h2><p className="mono">{data.id}</p></div>
        <div className="panel-actions">{canOperate && ['DEAD_LETTERED', 'DELIVERY_FAILED'].includes(data.status) ? <button type="button" onClick={() => void handleResend()}>Resend</button> : null}<button type="button" onClick={onClose}>Close</button></div>
      </div>
      <div className="summary-grid">
        <div><strong>AMP</strong><span className="mono">{data.numeroAMP}</span></div>
        <div><strong>Identifiant</strong><span className="mono">{data.identifiant}</span></div>
        <div><strong>Status</strong><span><StatusBadge value={data.status} /></span></div>
        <div><strong>Image count</strong><span>{data.imageCount}</span></div>
        <div><strong>Correlation ID</strong><span className="inline-copy"><span className="mono">{data.correlationId ?? 'n/a'}</span><CopyButton value={data.correlationId} label="Copy correlation ID" /></span></div>
      </div>
      {data.failureReason ? <div className="form-error">{data.failureReason}</div> : null}
      {actionError ? <div className="form-error">{actionError}</div> : null}
      <h3>Attempt history</h3><JsonBlock value={attempts ?? []} />
      <h3>Images (stored in object storage — never as base64 in Postgres)</h3>
      <table className="data-table">
        <thead><tr><th>#</th><th>MIME type</th><th>Size</th><th>SHA-256</th><th>Status</th><th></th></tr></thead>
        <tbody>
          {data.images.map((image) => (
            <tr key={image.id}>
              <td>{image.imageIndex + 1}</td>
              <td>{image.mimeType}</td>
              <td>{image.byteSize} bytes</td>
              <td className="mono">{image.sha256.slice(0, 16)}…</td>
              <td><StatusBadge value={image.status} /></td>
              <td>
                {image.status === 'UPLOADED' ? (
                  <button type="button" className="link-button" onClick={() => void handleDownload(image.id)}>
                    {imageUrls[image.id] ? 'Re-download' : 'Download'}
                  </button>
                ) : null}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  );
}
